import classnames from 'classnames';
import * as React from 'react';

/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { ProductPrice } from '@msdyn365-commerce/retail-proxy';

export interface IProductDetailsPriceComponentProps extends IComponentProps<{price: ProductPrice}> {
    className?: string;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
}

export interface IProductDetailsPriceComponent extends IComponent<IProductDetailsPriceComponentProps> {
}

const PriceComponentActions = {
};

const Price: React.FC<IProductDetailsPriceComponentProps> = (props: IProductDetailsPriceComponentProps) => {
    // CustomerContextualPrice could be 0
    if (props.data.price.CustomerContextualPrice === undefined) {
        return null;
    }

    return (
        <span className={classnames('msc-price', props.className)}>
            {showStrikethroughPricing(props) ? renderCurrentPriceWithOriginalPrice(props) : renderCurrentPrice(props)}
        </span>
    );
};

const showStrikethroughPricing = (props: IProductDetailsPriceComponentProps) => {
    const originalPrice = getOriginalPrice(props);

    if (originalPrice && props.data.price.CustomerContextualPrice) {
        return originalPrice > props.data.price.CustomerContextualPrice;
    }

    return false;
};

const getOriginalPrice = (props:IProductDetailsPriceComponentProps) => {
    return Math.max(props.data.price.BasePrice || 0, props.data.price.TradeAgreementPrice || 0, props.data.price.AdjustedPrice || 0);
};

const renderCurrentPrice = (props: IProductDetailsPriceComponentProps): JSX.Element | undefined => {
    const initialPrice = formatCurrency(props, props.data.price.CustomerContextualPrice);
    return <span className='msc-price__actual' itemProp='price'>{initialPrice}</span>;
};

const formatCurrency = (props: IProductDetailsPriceComponentProps, price: number | undefined): string | undefined => {
    if (price === undefined) {
        return undefined;
    }

    if (price === 0 && props.freePriceText) {
        return props.freePriceText;
    }

    return props.context.cultureFormatter.formatCurrency(price);
};

const renderCurrentPriceWithOriginalPrice = (props: IProductDetailsPriceComponentProps): JSX.Element | null => {
    const originalPrice = getOriginalPrice(props);
    const initialPrice = formatCurrency(props, originalPrice);

    return (
        <>
            <span className='sr-only'> {props.originalPriceText} {initialPrice} {props.currentPriceText} {renderCurrentPrice(props)}</span>
            <span className='msc-price__strikethrough' aria-hidden='true'>{initialPrice}</span>
            <span aria-hidden='true'>{renderCurrentPrice(props)}</span>
            {props.savingsText && <span className='msc-price__savings'>{props.savingsText}</span>}
        </>
    );
};

// @ts-ignore
export const ProductDetailsPriceComponent: React.FunctionComponent<IPriceComponentProps> = msdyn365Commerce.createComponent<IPriceComponent>(
    'Price',
    { component: Price, ...PriceComponentActions }
);